import type { CSSProperties } from "react";
import type {
  BreakpointPadding,
  ContainerPadding,
  ItemsPerView,
  PaddingEdges,
} from "./types";

export function resolveItemsPerView(itemsPerView: ItemsPerView) {
  if (typeof itemsPerView === "number") {
    return {
      "--mobile-items": itemsPerView,
      "--tablet-items": itemsPerView,
      "--desktop-items": itemsPerView,
    } as CSSProperties;
  }

  return {
    "--mobile-items": itemsPerView.mobile ?? 1,
    "--tablet-items": itemsPerView.tablet ?? 3,
    "--desktop-items": itemsPerView.desktop ?? 6,
  } as Record<string, number>;
}

function isBreakpointPadding(
  p: ContainerPadding | string
): p is BreakpointPadding {
  return (
    typeof p === "object" && ("mobile" in p || "tablet" in p || "desktop" in p)
  );
}

export function resolveContainerPadding(padding: ContainerPadding) {
  const allEdges = (value: string): PaddingEdges => ({
    inlineStart: value,
    inlineEnd: value,
    blockStart: value,
    blockEnd: value,
  });

  const { mobile, tablet, desktop }: BreakpointPadding = isBreakpointPadding(
    padding
  )
    ? {
        mobile: padding.mobile ?? {},
        tablet: padding.tablet ?? {},
        desktop: padding.desktop ?? {},
      }
    : typeof padding === "string"
    ? {
        mobile: allEdges(padding),
        tablet: allEdges(padding),
        desktop: allEdges(padding),
      }
    : { mobile: padding, tablet: padding, desktop: padding };

  const vars: Record<string, string> = {};

  const edges: [keyof PaddingEdges, string][] = [
    ["inlineStart", "inline-start"],
    ["inlineEnd", "inline-end"],
    ["blockStart", "block-start"],
    ["blockEnd", "block-end"],
  ];

  const mainKey = "parent-padding";

  for (const [key, cssKey] of edges) {
    const mobileVal = mobile[key];
    if (mobileVal) {
      vars[`--mobile-${mainKey}-${cssKey}`] = mobileVal;
      continue;
    }

    const tabletVal = tablet[key];
    if (tabletVal) {
      vars[`--tablet-${mainKey}-${cssKey}`] = tabletVal;
      continue;
    }

    const desktopVal = desktop[key];
    if (desktopVal) {
      vars[`--desktop-${mainKey}-${cssKey}`] = desktopVal;
      continue;
    }
  }

  return vars as CSSProperties;
}
